import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './EmailConfirmationModal.styles';
import { EmailConfirmationCodeInputs } from './EmailConfirmationCodeInputs';
import { EmailConfirmationShell } from './EmailConfirmationShell';
import { useEmailConfirmationModal } from '@/features/Profile/sections/ProfileSettings/hooks';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { WarningModal } from '@/components/Ad/WarningModal';

interface EmailConfirmationModalProps {
  email: string;
  isOpen: boolean;
  onClose: () => void;
}

export const EmailConfirmationModal = ({
  email,
  isOpen,
  onClose,
}: EmailConfirmationModalProps) => {
  const { t } = useTranslation();
  const {
    code,
    errorMessage,
    isCodeExpired,
    isCodeInputDisabled,
    isResendVisible,
    isSuccessOpen,
    timerText,
    isTimerVisible,
    handleClose,
    handleSuccessClose,
    setCodeInputRef,
    handleCodeChange,
    handleCodeKeyDown,
    handleCodePaste,
    requestCode,
  } = useEmailConfirmationModal({ email, isOpen, onClose });

  return (
    <>
      <EmailConfirmationShell
        isOpen={isOpen}
        onClose={handleClose}
        title={t('pages.cabinet.email_confirmation.title')}
      >
        <S.Content>
          <Typography.Text>
            {t('pages.cabinet.email_confirmation.sent_code', {
              email,
            })}
          </Typography.Text>

          <S.CodeInputsContainer>
            <EmailConfirmationCodeInputs
              code={code}
              isDisabled={isCodeInputDisabled}
              setInputRef={setCodeInputRef}
              onChange={handleCodeChange}
              onKeyDown={handleCodeKeyDown}
              onPaste={handleCodePaste}
            />

            {errorMessage && <S.ErrorText>{errorMessage}</S.ErrorText>}
            {isCodeExpired && (
              <S.ErrorText>
                {t('pages.cabinet.email_confirmation.code_expired')}
              </S.ErrorText>
            )}
          </S.CodeInputsContainer>

          <Typography.Text>
            {t('pages.cabinet.email_confirmation.help')}
          </Typography.Text>

          <S.TimerAction>
            {isTimerVisible && <S.TimerText>{timerText}</S.TimerText>}
            {isResendVisible && (
              <S.ResendButton type="button" onClick={requestCode}>
                {t('pages.cabinet.email_confirmation.resend_code')}
              </S.ResendButton>
            )}
          </S.TimerAction>
        </S.Content>
      </EmailConfirmationShell>

      <WarningModal
        isOpen={isSuccessOpen}
        onClose={handleSuccessClose}
        title={t('pages.cabinet.email_confirmation.success_title')}
        description={t('pages.cabinet.email_confirmation.success_description', {
          email,
        })}
        icon="check-circle-rounded.svg"
      />
    </>
  );
};
